import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../supabase/supabase';
import SafeIcon from '../../common/SafeIcon';
import { QUESTIONNAIRE_SECTIONS as DEFAULT_SECTIONS } from '../../data/questionnaire';

const FormSchemaEditor = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dbRecordId, setDbRecordId] = useState(null);
  const jsonInputRef = useRef(null);

  useEffect(() => {
    loadSchema();
  }, []);

  const loadSchema = async () => {
    try {
      const { data, error } = await supabase.from('form_schema_2024').select('*').maybeSingle();
      if (data?.schema_data) {
        setSections(data.schema_data);
        setDbRecordId(data.id);
      } else {
        setSections(DEFAULT_SECTIONS);
      }
    } catch (err) {
      console.error("Schema load error:", err);
      setSections(DEFAULT_SECTIONS);
    } finally {
      setLoading(false);
    }
  };

  const saveSchema = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('form_schema_2024')
        .upsert({
          id: dbRecordId || undefined,
          schema_data: sections,
          updated_at: new Date().toISOString()
        });
      if (error) throw error;
      alert('Form structure deployed successfully!');
      loadSchema();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const exportSchemaJSON = () => {
    const dataStr = JSON.stringify(sections, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `growth_schema_${new Date().toISOString().split('T')[0]}.json`;
    link.click();
  };

  const handleImportJSON = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const imported = JSON.parse(event.target.result);
        if (Array.isArray(imported)) {
          setSections(imported);
        }
      } catch (err) {
        alert("Invalid JSON file.");
      }
    };
    reader.readAsText(file);
  };

  const updateSection = (index, updates) => {
    const newSections = [...sections];
    newSections[index] = { ...newSections[index], ...updates };
    setSections(newSections);
  };

  const updateField = (sIdx, fIdx, updates) => {
    const newSections = [...sections];
    newSections[sIdx].fields[fIdx] = { ...newSections[sIdx].fields[fIdx], ...updates };
    setSections(newSections);
  };

  const addField = (sIdx) => {
    const newField = {
      name: `field_${Date.now()}`,
      label: "New Question",
      type: "text",
      width: "full",
      placeholder: "Your answer here...",
      helperText: "Instructional guidance...",
      required: false
    };
    const newSections = [...sections];
    newSections[sIdx].fields.push(newField);
    setSections(newSections);
  };

  if (loading) return <div className="p-20 text-center animate-pulse text-slate-400 font-black">SYNCING BUILDER...</div>;

  return (
    <div className="space-y-8 pb-32">
      <input type="file" ref={jsonInputRef} onChange={handleImportJSON} accept=".json" className="hidden" />

      {/* Builder Header */}
      <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center bg-white p-8 rounded-[40px] border border-slate-200 shadow-sm gap-6">
        <div>
          <h2 className="text-2xl font-black text-slate-900 tracking-tight">Form Intelligence Builder</h2>
          <p className="text-slate-500 text-sm font-medium">Control every word, tooltip, and instruction in the discovery flow.</p>
        </div>
        <div className="flex flex-wrap gap-3 w-full xl:w-auto">
          <button onClick={() => jsonInputRef.current?.click()} className="px-5 py-3 bg-slate-50 text-slate-700 rounded-2xl font-bold text-xs hover:bg-slate-100 transition-all flex items-center gap-2">
            <SafeIcon name="Upload" className="w-4 h-4" /> Import
          </button>
          <button onClick={exportSchemaJSON} className="px-5 py-3 bg-slate-50 text-slate-700 rounded-2xl font-bold text-xs hover:bg-slate-100 transition-all flex items-center gap-2">
            <SafeIcon name="Download" className="w-4 h-4" /> Export
          </button>
          <button onClick={saveSchema} disabled={saving} className="flex-1 xl:flex-none bg-blue-600 text-white px-8 py-4 rounded-[22px] font-black text-sm hover:bg-blue-700 transition-all shadow-xl shadow-blue-100 flex items-center justify-center gap-2">
            {saving ? 'Saving...' : <><SafeIcon name="Zap" className="w-4 h-4" /> Deploy Changes</>}
          </button>
        </div>
      </div>

      {/* Sections List */}
      <div className="space-y-10">
        {sections.map((section, sIdx) => (
          <div key={section.id || sIdx} className="bg-white rounded-[48px] border border-slate-200 p-10 shadow-sm group">
            <div className="space-y-8 mb-10">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                <div className="md:col-span-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block ml-1">Icon</label>
                  <input 
                    className="w-full bg-slate-50 border border-slate-100 rounded-2xl px-5 py-4 text-sm font-bold text-slate-700 outline-none focus:ring-4 focus:ring-blue-500/10"
                    value={section.icon || ''}
                    onChange={(e) => updateSection(sIdx, { icon: e.target.value })}
                    placeholder="Briefcase"
                  />
                </div>
                <div className="md:col-span-10">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block ml-1">Section Title</label>
                  <input 
                    className="w-full bg-slate-50 border border-slate-100 rounded-2xl px-6 py-4 text-xl font-black text-slate-900 outline-none focus:ring-4 focus:ring-blue-500/10"
                    value={section.title}
                    onChange={(e) => updateSection(sIdx, { title: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block ml-1">Consultant Message (Introduction to this section)</label>
                <textarea 
                  className="w-full bg-blue-50/30 border border-blue-100 rounded-[28px] px-6 py-5 text-sm font-medium text-slate-600 outline-none focus:ring-4 focus:ring-blue-500/5 transition-all"
                  value={section.consultantMessage || ''}
                  onChange={(e) => updateSection(sIdx, { consultantMessage: e.target.value })}
                  rows={2}
                />
              </div>
            </div>

            {/* Fields List */}
            <div className="space-y-4 mb-8">
              {section.fields?.map((field, fIdx) => (
                <div key={fIdx} className="bg-slate-50/50 p-8 rounded-[32px] border border-slate-100 space-y-6 group/field hover:bg-white transition-all">
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                    <div className="md:col-span-5">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block ml-1">The Question (Label)</label>
                      <input className="w-full bg-white border border-slate-200 rounded-xl px-5 py-3 text-sm font-bold text-slate-900" value={field.label} onChange={(e) => updateField(sIdx, fIdx, { label: e.target.value })} />
                    </div>
                    <div className="md:col-span-3">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block ml-1">Input Type</label>
                      <select className="w-full bg-white border border-slate-200 rounded-xl px-5 py-3 text-sm font-bold" value={field.type} onChange={(e) => updateField(sIdx, fIdx, { type: e.target.value })}>
                        <option value="text">Short Answer</option>
                        <option value="textarea">Long Answer</option>
                        <option value="select">Multiple Choice</option>
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block ml-1">Required?</label>
                      <button 
                        onClick={() => updateField(sIdx, fIdx, { required: !field.required })}
                        className={`w-full py-3 rounded-xl font-bold text-xs uppercase tracking-widest border transition-all ${field.required ? 'bg-emerald-500 border-emerald-600 text-white' : 'bg-white border-slate-200 text-slate-400'}`}
                      >
                        {field.required ? 'YES' : 'NO'}
                      </button>
                    </div>
                    <div className="md:col-span-2 flex items-end">
                      <button onClick={() => { const newFields = [...section.fields]; newFields.splice(fIdx, 1); updateSection(sIdx, { fields: newFields }); }} className="w-full p-3 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-xl border border-slate-200 transition-all">
                        <SafeIcon name="Trash2" className="w-4 h-4 mx-auto" />
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block ml-1">Helper Text (The Tooltip ?)</label>
                      <input 
                        className="w-full bg-white border border-slate-200 rounded-xl px-5 py-3 text-sm font-medium text-slate-600" 
                        value={field.helperText || ''} 
                        onChange={(e) => updateField(sIdx, fIdx, { helperText: e.target.value })} 
                        placeholder="Explain how to answer this question..."
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block ml-1">Input Placeholder</label>
                      <input 
                        className="w-full bg-white border border-slate-200 rounded-xl px-5 py-3 text-sm font-medium text-slate-600" 
                        value={field.placeholder || ''} 
                        onChange={(e) => updateField(sIdx, fIdx, { placeholder: e.target.value })} 
                        placeholder="e.g. Your answer here..."
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <button onClick={() => addField(sIdx)} className="w-full py-6 border-2 border-dashed border-slate-200 rounded-[32px] text-slate-400 font-black text-xs uppercase tracking-widest hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50/50 transition-all flex items-center justify-center gap-3">
              <SafeIcon name="Plus" className="w-4 h-4" /> Add Question to this Section
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FormSchemaEditor;